import React, { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import slidingPuzzleQuestIcon from "../../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import {
    SHOW_SLIDING_APP_STORE_BADGE,
    SHOW_SLIDING_GOOGLE_PLAY_BADGE,
} from "../../../config/nativeAppsVisibility";
import HomeService from "../../../services/HomeService";
import UserService from "../../../services/UserService";
import UtilService from "../../../services/UtilService";
import Logger from "../../../services/Logger";
import IconBack from "../../components/IconBack";
import AppStoreBadgeLink from "../../components/AppStoreBadgeLink";
import AppStorePromoPill from "../../components/AppStorePromoPill";
import GooglePlayBadgeLink from "../../components/GooglePlayBadgeLink";
import SubscriptionDialog from "../../subscription/SubscriptionDialog";
import { usePagesTranslation } from "../../locales";
import { TagChipAvatar } from "../common/components/TagChipAvatar";
import { buildPuzzleAccessContext } from "../common/puzzleTierAccess";
import {
    getSlidingStoryTitle,
    pickSlidingBrowseBoardPreview,
    pickSlidingPlayImageUrl,
} from "../../browse-sliding/slidingBrowseUtils";
import SlidingHowToMoveExample from "./SlidingHowToMoveExample";
import SlidingLevelGroup from "./SlidingLevelGroup";
import SlidingPlayMistBackground from "./SlidingPlayMistBackground";
import { SITE_BG } from "../../../dark-theme/palette";
import { trackEvent } from "../../../services/GoogleAnalytics";
import {
    resolveSlidingDefaultTemplateId,
    saveSlidingPrefsFromTemplate,
} from "../common/puzzlePlayPrefs";
import { DIFF_LEVELS, templateForGridAndLevel } from "../../../admin/story/adminSlidingUi";
import { JIGSAW_PLAY_WARM } from "../jigsaw/TemplateLevelGroup";

const GRID_SIZES = [4, 6];

/** Header text sits straight on the night mist, so it runs light. */
const MIST_HEADING_COLOR = "#eaf6ef";
const MIST_BODY_COLOR = "rgba(226,232,240,0.82)";
/** Copy inside the frosted white panels — dark theme `text.*` is unreadable there. */
const PANEL_INK = "#163028";
const PANEL_MUTED = "rgba(22, 48, 40, 0.78)";

/** Fixed story used for the “how to move tiles” scrambled-board example. */
const HOW_TO_MOVE_EXAMPLE_STORY_ID = "2c779fbf-8002-46e6-aa9e-530521d1cc80";

const PANEL_PAPER_SX = {
    p: { xs: 1.5, sm: 2 },
    bgcolor: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(10px)",
    borderColor: "rgba(255,255,255,0.55)",
    boxShadow: "0 8px 28px rgba(40, 70, 55, 0.12)",
    color: PANEL_INK,
};

const PLAY_BUTTON_SX = {
    background: `linear-gradient(165deg, #E0A85A 0%, ${JIGSAW_PLAY_WARM.main} 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
    color: "#1a1208",
    px: 4,
    minWidth: 140,
    minHeight: 44,
    textTransform: "none",
    fontWeight: 700,
    letterSpacing: "0.04em",
    boxShadow: `0 4px 14px ${JIGSAW_PLAY_WARM.soft}, inset 0 1px 0 rgba(255,255,255,0.28)`,
    transition: "transform 160ms ease, box-shadow 160ms ease, filter 160ms ease",
    "&:hover": {
        background: `linear-gradient(165deg, #E8B56A 0%, #D49644 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
        boxShadow: `0 8px 22px ${JIGSAW_PLAY_WARM.glow}, inset 0 1px 0 rgba(255,255,255,0.35)`,
        filter: "brightness(1.04)",
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "translateY(0)",
        boxShadow: `0 2px 10px ${JIGSAW_PLAY_WARM.soft}`,
    },
};

/** Prefer Medium (4×4, then 6×6); then any linked template. */
function resolveDefaultTemplateId(templates, cloudUserId) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    if (cloudUserId) {
        return resolveSlidingDefaultTemplateId(templates, cloudUserId);
    }
    const fourMed = templateForGridAndLevel(templates, 4, "MEDIUM");
    if (fourMed?.id) return fourMed.id;
    const sixMed = templateForGridAndLevel(templates, 6, "MEDIUM");
    if (sixMed?.id) return sixMed.id;
    for (const gs of GRID_SIZES) {
        for (const level of DIFF_LEVELS) {
            const tpl = templateForGridAndLevel(templates, gs, level);
            if (tpl?.id) return tpl.id;
        }
    }
    return null;
}

function pickMediumTemplate(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    return (
        templateForGridAndLevel(templates, 4, "MEDIUM")
        || templateForGridAndLevel(templates, 6, "MEDIUM")
        || null
    );
}

export default function SlidingSelectLevelPage() {
    const { storyId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = usePagesTranslation();
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [difficultyDialogOpen, setDifficultyDialogOpen] = useState(false);
    const [howToExamplePreview, setHowToExamplePreview] = useState(null);
    const prefsAppliedStoryRef = useRef(null);

    const playRecordPuzzleId = selectedTemplate?.id ? String(selectedTemplate.id).trim() : null;

    useEffect(() => {
        trackEvent("entered", {
            name: "SlidingSelectLevelPage",
            puzzle_type: "SLIDING",
            page_kind: "select",
            story_id: storyId ?? undefined,
            puzzle_id: playRecordPuzzleId ?? undefined,
        });
    }, [storyId, playRecordPuzzleId]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const [data, userData] = await Promise.all([
                    storyId ? HomeService.getSlidingStoryGroup(storyId) : Promise.resolve(null),
                    UserService.getUserInfo().catch(() => null),
                ]);
                if (!cancelled) {
                    setGroup(data);
                    setUserInfo(userData);
                }
            } catch (e) {
                Logger.error("SlidingSelectLevelPage:", e);
            }
            if (!cancelled) setLoading(false);
        }
        if (storyId) load();
        else setLoading(false);
        return () => { cancelled = true; };
    }, [storyId]);

    useEffect(() => {
        let cancelled = false;
        async function loadHowToExample() {
            try {
                const exampleGroup = await HomeService.getSlidingStoryGroup(HOW_TO_MOVE_EXAMPLE_STORY_ID);
                if (cancelled) return;
                setHowToExamplePreview(pickSlidingBrowseBoardPreview(exampleGroup));
            } catch (e) {
                Logger.error("SlidingSelectLevelPage how-to example:", e);
                if (!cancelled) setHowToExamplePreview(null);
            }
        }
        loadHowToExample();
        return () => {
            cancelled = true;
        };
    }, []);

    const cloudUserId = userInfo?.cloudUser?.id ?? null;

    useEffect(() => {
        prefsAppliedStoryRef.current = null;
    }, [storyId]);

    useEffect(() => {
        if (!storyId || !group?.templates?.length) return;
        const appliedKey = `${storyId}:${cloudUserId ?? "guest"}`;
        if (prefsAppliedStoryRef.current === appliedKey) return;
        prefsAppliedStoryRef.current = appliedKey;
        const defaultId = resolveDefaultTemplateId(group.templates, cloudUserId);
        const tpl = defaultId
            ? group.templates.find((t) => t.id === defaultId) ?? null
            : null;
        if (tpl) setSelectedTemplate(tpl);
    }, [storyId, group, cloudUserId]);

    useEffect(() => {
        if (!cloudUserId || !selectedTemplate) return;
        saveSlidingPrefsFromTemplate(cloudUserId, selectedTemplate);
    }, [cloudUserId, selectedTemplate]);

    const story = group?.story ?? null;
    const templates = useMemo(() => group?.templates ?? [], [group]);
    const title = getSlidingStoryTitle(story) || group?.puzzleTemplate?.title || "";
    const puzzleTags = (group?.puzzleTemplate?.tags || [])
        .filter((tag) => tag.toLowerCase() !== "bedrock")
        .map((tag) => String(tag).trim().toLowerCase());
    const imageUrl = useMemo(() => pickSlidingPlayImageUrl(story), [story]);
    const accessContext = useMemo(() => buildPuzzleAccessContext(userInfo), [userInfo]);
    const userPayPlan = UtilService.resolveWebPayPlan(userInfo?.cloudUser);

    // Group templates by grid size
    const gridSizesPresent = useMemo(() => {
        const sizes = new Set();
        templates.forEach((t) => {
            const gs = Number(t.gridSize);
            if (gs > 0) sizes.add(gs);
        });
        return GRID_SIZES.filter((gs) => sizes.has(gs));
    }, [templates]);

    const levelEntries = useMemo(() => {
        const entries = [];
        for (const gs of gridSizesPresent) {
            for (const level of DIFF_LEVELS) {
                const tpl = templateForGridAndLevel(templates, gs, level);
                if (tpl) entries.push({ gridSize: gs, level, template: tpl });
            }
        }
        return entries;
    }, [templates, gridSizesPresent]);

    const handleBack = () => {
        if (location.state?.fromLibrary === true) {
            navigate("/account/library", {
                state: { activeTab: location.state?.activeTab ?? 0 },
            });
            return;
        }
        navigate("/puzzles/sliding");
    };

    return (
        <Box
            sx={{
                position: "relative",
                isolation: "isolate",
                width: "100%",
                flex: 1,
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
                overflowY: "auto",
                bgcolor: SITE_BG,
            }}
        >
            <SlidingPlayMistBackground variant="night" />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    p: { xs: 2, sm: 3 },
                    maxWidth: 1100,
                    width: "100%",
                    mx: "auto",
                    boxSizing: "border-box",
                }}
            >
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !group ? (
                <Typography sx={{ color: MIST_BODY_COLOR }}>Puzzle not found.</Typography>
            ) : (
                <>
                    <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                        <IconBack onClick={handleBack} sx={{ alignSelf: "flex-start", mt: 0.5 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: MIST_HEADING_COLOR,
                                    fontWeight: 600,
                                    mb: puzzleTags.length ? 0.75 : 0.5,
                                }}
                            >
                                {title || "Sliding Puzzle"}
                            </Typography>
                            {puzzleTags.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 0.75 }}>
                                    {puzzleTags.map((tag) => (
                                        <Chip
                                            key={tag}
                                            avatar={<TagChipAvatar tag={tag} size={18} />}
                                            label={tag}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: "0.7rem",
                                                height: 20,
                                                bgcolor: "rgba(255,255,255,0.55)",
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    </Box>

                    {levelEntries.length === 0 ? (
                        <Typography sx={{ color: MIST_BODY_COLOR }}>
                            No templates available.
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2.5,
                                width: "100%",
                                maxWidth: 720,
                                mx: "auto",
                            }}
                        >
                            <Paper variant="outlined" sx={{ ...PANEL_PAPER_SX, width: "100%" }}>
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        bgcolor: "grey.900",
                                    }}
                                >
                                    {imageUrl ? (
                                        <Box
                                            component="img"
                                            src={imageUrl}
                                            alt={title || ""}
                                            referrerPolicy="no-referrer"
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                display: "block",
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ color: PANEL_MUTED }}>
                                                —
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>

                            <Button
                                variant="contained"
                                onClick={() => {
                                    const medium = pickMediumTemplate(templates);
                                    const fallbackId = resolveDefaultTemplateId(templates, null);
                                    const next =
                                        medium
                                        || templates.find((tpl) => tpl?.id === fallbackId)
                                        || templates[0]
                                        || null;
                                    if (next) setSelectedTemplate(next);
                                    setDifficultyDialogOpen(true);
                                }}
                                sx={{
                                    ...PLAY_BUTTON_SX,
                                    visibility: difficultyDialogOpen ? "hidden" : "visible",
                                }}
                            >
                                {t("puzzle.card.play")}
                            </Button>
                        </Box>
                    )}

                    <Dialog
                        open={difficultyDialogOpen && levelEntries.length > 0}
                        onClose={() => setDifficultyDialogOpen(false)}
                        maxWidth="sm"
                        fullWidth
                        aria-labelledby="sliding-difficulty-dialog-title"
                    >
                        <DialogTitle
                            id="sliding-difficulty-dialog-title"
                            sx={{ pr: 6, fontWeight: 700 }}
                        >
                            {t("puzzle.sliding.select-difficulty")}
                            <IconButton
                                aria-label={t("common.close", "Close")}
                                onClick={() => setDifficultyDialogOpen(false)}
                                sx={{
                                    position: "absolute",
                                    right: 8,
                                    top: 8,
                                    minWidth: 44,
                                    minHeight: 44,
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t(
                                    "puzzle.sliding.difficulty-dialog-hint",
                                    "Medium is selected by default. Choose grid size and difficulty, then tap Play.",
                                )}
                            </Typography>
                            <SlidingLevelGroup
                                levelEntries={levelEntries}
                                selectedTemplate={selectedTemplate}
                                setSelectedTemplate={setSelectedTemplate}
                                accessContext={accessContext}
                                cloudUserId={cloudUserId}
                                isFreePuzzle={group?.storySliding?.free === true}
                                onUpgrade={() => {
                                    setDifficultyDialogOpen(false);
                                    setSubscriptionModalOpen(true);
                                }}
                                navigationState={location.state}
                            />
                        </DialogContent>
                    </Dialog>

                    {!loading && group && howToExamplePreview ? (
                        <Box sx={{ mt: 3 }}>
                            <Paper variant="outlined" sx={PANEL_PAPER_SX}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 700, color: "#2d6b4f", mb: 1 }}
                                >
                                    {t("puzzle.sliding.how-to-move-title", "How to move tiles")}
                                </Typography>
                                <Box
                                    component="ul"
                                    sx={{
                                        m: 0,
                                        mb: 2,
                                        pl: 2.25,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.75,
                                    }}
                                >
                                    <Typography
                                        component="li"
                                        variant="body2"
                                        sx={{ lineHeight: 1.5, color: PANEL_MUTED }}
                                    >
                                        {t(
                                            "puzzle.sliding.how-to-move-step-1",
                                            "On the play board, one cell is empty (the gap).",
                                        )}
                                    </Typography>
                                    <Typography
                                        component="li"
                                        variant="body2"
                                        sx={{ lineHeight: 1.5, color: PANEL_MUTED }}
                                    >
                                        {t(
                                            "puzzle.sliding.how-to-move-step-2",
                                            "Tap a tile next to the empty cell — it slides into the gap.",
                                        )}
                                    </Typography>
                                    <Typography
                                        component="li"
                                        variant="body2"
                                        sx={{ lineHeight: 1.5, color: PANEL_MUTED }}
                                    >
                                        {t(
                                            "puzzle.sliding.how-to-move-step-3",
                                            "Only up, down, left, or right neighbors can move — no diagonals.",
                                        )}
                                    </Typography>
                                    <Typography
                                        component="li"
                                        variant="body2"
                                        sx={{ lineHeight: 1.5, color: PANEL_MUTED }}
                                    >
                                        {t(
                                            "puzzle.sliding.how-to-move-step-4",
                                            "Keep sliding until the picture matches the original grid above.",
                                        )}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        fontWeight: 700,
                                        color: PANEL_MUTED,
                                        letterSpacing: "0.02em",
                                        mb: 1,
                                    }}
                                >
                                    {t(
                                        "puzzle.sliding.how-to-move-example-label",
                                        "Play board example (scrambled + empty cell)",
                                    )}
                                </Typography>
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        maxWidth: { xs: "100%", sm: 360 },
                                        mx: "auto",
                                    }}
                                >
                                    <SlidingHowToMoveExample
                                        imageUrl={howToExamplePreview.imageUrl}
                                        gridSize={howToExamplePreview.gridSize}
                                        startingLayout={howToExamplePreview.startingLayout}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    ) : null}

                    {SHOW_SLIDING_APP_STORE_BADGE || SHOW_SLIDING_GOOGLE_PLAY_BADGE ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 3,
                                mb: 1,
                            }}
                        >
                            <AppStorePromoPill
                                iconSrc={slidingPuzzleQuestIcon}
                                appName="Sliding Puzzle Quest"
                                tagline={t(
                                    "marketing.sliding-quest-promo",
                                    "Sliding tile puzzles with beautiful photo grids.",
                                )}
                            >
                                {SHOW_SLIDING_APP_STORE_BADGE ? (
                                    <AppStoreBadgeLink height={36} />
                                ) : null}
                                {SHOW_SLIDING_GOOGLE_PLAY_BADGE ? (
                                    <GooglePlayBadgeLink height={36} />
                                ) : null}
                            </AppStorePromoPill>
                        </Box>
                    ) : null}
                </>
            )}

            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={() => setSubscriptionModalOpen(false)}
                onSubscribe={() => { setSubscriptionModalOpen(false); window.location.reload(); }}
                triggerReason="puzzle"
                userSubscription={userPayPlan || "free"}
            />
            </Box>
        </Box>
    );
}
